import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, InfoIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { UnitShowcase } from '../components/UnitShowcase';
import { Reveal } from '../components/Reveal';
import { fees, properties, stayTimes } from '../data/units';
import { formatNaira } from '../utils/format';
const extras = [{
  label: 'Early check-in',
  detail: 'Arrive before 2 PM — charged only if approved.',
  price: `${formatNaira(fees.earlyLateMin)}–${formatNaira(fees.earlyLateMax)}`
}, {
  label: 'Late checkout',
  detail: 'Stay beyond 12 noon — charged only if approved.',
  price: `${formatNaira(fees.earlyLateMin)}–${formatNaira(fees.earlyLateMax)}`
}, {
  label: 'Photoshoot session',
  detail: `${fees.photoshootHours}-hour block at the property.`,
  price: formatNaira(fees.photoshootSession)
}, {
  label: 'Refundable caution fee',
  detail: 'Mandatory, collected upfront on every apartment booking.',
  price: formatNaira(fees.cautionFee)
}];
export function Accommodations() {
  return <>
      <PageHeader eyebrow="Apartments" title="Thoughtful design, exceptional comfort" intro={`Every apartment shares the same standard — pool, generator-backed power and 24-hour internet. Check-in ${stayTimes.checkIn}, checkout ${stayTimes.checkOut}.`} data-id="element-364" data-mp="18115xyp1uy2x" />

      <section className="bg-ivory-100" data-id="element-365" data-mp="1cum71zlxw86c">
        <div className="mx-auto max-w-[1240px] space-y-24 px-5 py-20 sm:px-8 lg:space-y-32 lg:px-12 lg:py-28" data-id="element-366" data-mp="p3zxg3waefu8">
          {properties.map((unit, index) => <div key={unit.id} id={unit.id} className="scroll-mt-28" data-id="element-367" data-mp="l4hehdo10eu1" data-mpi={index}>
              <UnitShowcase unit={unit} reversed={index % 2 === 1} data-id="element-368" data-mp="z3h70wentdvh" />
            </div>)}
        </div>
      </section>

      <section className="bg-ivory-200" data-id="element-369" data-mp="u21iuvh6qr8v">
        <div className="mx-auto max-w-[1240px] px-5 py-20 sm:px-8 lg:px-12 lg:py-24" data-id="element-370" data-mp="2ahcm655kdmmp">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20" data-id="element-371" data-mp="25ii1syry7q0h">
            <Reveal data-id="element-372" data-mp="25km2z8ygqax2">
              <p className="flex items-center gap-3 text-[11px] uppercase tracking-wordmark text-gold-700" data-id="element-373" data-mp="2dj7i2x95bxr3">
                <span className="h-px w-8 bg-gold-500" aria-hidden="true" data-id="element-374" data-mp="2hkj4yv0my4vl" />
                Rates & fees
              </p>
              <h2 className="mt-4 font-display text-3xl leading-tight text-forest-700 sm:text-[2.6rem]" data-id="element-375" data-mp="2mkxao6erl2qg">What your stay costs</h2>
              <p className="mt-5 text-[15px] leading-relaxed text-muted" data-id="element-376" data-mp="2ri78oag7ympw">
                Your total is accommodation plus any approved extras plus the {formatNaira(fees.cautionFee)} refundable caution fee.
                Final amounts are confirmed by our booking system when you submit.
              </p>
              <div className="mt-6 rounded-sm bg-ivory-50 p-5 text-[13px] leading-relaxed text-ink shadow-soft" data-id="element-377" data-mp="23rb9utdcir95">
                <p className="flex items-center gap-2 font-normal text-forest-700" data-id="element-378" data-mp="28lxp57mjt3yi">
                  <InfoIcon size={15} className="text-gold-500" aria-hidden="true" data-id="element-379" data-mp="1b2mi3ky6jya2" />
                  Example — 3-bedroom, 2 nights
                </p>
                <p className="mt-2 text-muted" data-id="element-380" data-mp="18kkhnu3jz4t9">
                  {formatNaira(440000)} accommodation + {formatNaira(fees.cautionFee)} caution fee ={' '}
                  <span className="text-forest-700" data-id="element-381" data-mp="13jd75na0zriw">{formatNaira(540000)}</span>
                </p>
              </div>
              <Link to="/booking" className="group mt-8 inline-flex items-center gap-2 rounded-full bg-forest-700 px-6 py-3 text-sm text-ivory-100 transition-colors duration-150 ease-premium hover:bg-forest-600" data-id="element-382" data-mp="tna6ymyqi2yl">
                Start a booking
                <ArrowRightIcon size={15} className="transition-transform duration-200 ease-premium group-hover:translate-x-1" aria-hidden="true" data-id="element-383" data-mp="fvmyzpvfj5zz" />
              </Link>
            </Reveal>

            <Reveal delay={0.06} data-id="element-384" data-mp="aupidhq2ik0l">
              <ul className="divide-y divide-forest-700/10 border-y border-forest-700/10" data-id="element-385" data-mp="5xpad19nr76u">
                {properties.map(unit => <li key={unit.id} className="flex items-baseline justify-between gap-6 py-5" data-id="element-386" data-mp="1wdqzdhdni73">
                    <div data-id="element-387" data-mp="qn1mx0a4yeyp">
                      <p className="text-[15px] text-ink" data-id="element-388" data-mp="qouklq2rtouk">{unit.name}</p>
                      <p className="mt-1 text-[13px] text-muted" data-id="element-389" data-mp="vl323a4fxu4w">Max {unit.maxGuests} guests · per night</p>
                    </div>
                    <p className="shrink-0 font-display text-2xl text-forest-700" data-id="element-390" data-mp="lvs007fz1pqi">{formatNaira(unit.nightlyRate)}</p>
                  </li>)}
                {extras.map(extra => <li key={extra.label} className="flex items-baseline justify-between gap-6 py-5" data-id="element-391" data-mp="cv3mdduhpre5">
                    <div data-id="element-392" data-mp="3v0q2rwflptjm">
                      <p className="text-[15px] text-ink" data-id="element-393" data-mp="3v1hyr8d41taf">{extra.label}</p>
                      <p className="mt-1 text-[13px] text-muted" data-id="element-394" data-mp="2wnsj342w40y">{extra.detail}</p>
                    </div>
                    <p className="shrink-0 text-right text-[15px] text-gold-700" data-id="element-395" data-mp="1t9mrd4xw0ay0">{extra.price}</p>
                  </li>)}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>
    </>;
}